import { useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAuth } from "@/shared/hooks/use-auth";
import { ChatPage } from "@/modules/chat/components/ChatPage";
import { useChatStoreApi } from "@/modules/chat/stores/chat-store-context";

/**
 * Dedicated "Новая задача" / hero route.
 *
 * `/` is the smart landing — for admins it bounces to `/company`, so it
 * can't double as the new-task target. This route renders the chat hero
 * and is never redirected, so the sidebar button has a stable home for
 * every role. Existing sessions still live at `/?sid=`; this route is
 * purely the empty-state entry point.
 */
export const Route = createFileRoute("/_app/new")({
  component: NewRoute,
});

function NewRoute() {
  const auth = useAuth();
  const storeApi = useChatStoreApi();
  // Landing here means "start fresh": drop any active session so we show
  // hero even when navigated to from an open conversation. Do this ONCE
  // per mount — NOT every render. sendMessage flips activeSessionId and
  // pushes the user/assistant placeholder while we're still mounted on
  // `/new` (the `await createSession()` latency keeps the route here until
  // `navigate({sid})` commits). A render-time reset would re-run on that
  // re-render and wipe the just-pushed messages + null activeSessionId,
  // making routeEvent drop the whole live stream until the route finally
  // switches and history reloads. Mount-scoped reset preserves the
  // fresh-on-entry behavior without fighting an in-flight send.
  const didReset = useRef<true | null>(null);
  if (didReset.current == null) {
    didReset.current = true;
    if (storeApi.getState().activeSessionId !== null) {
      storeApi.getState().setActiveSession(null);
    }
  }
  return <ChatPage user={auth.user} />;
}
